import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Figure, Flow } from '../components/Diagram';

interface Row {
  k: ReactNode;
  v: ReactNode;
}

function Table({ rows }: { rows: Row[] }) {
  return (
    <div className="dp-fields-table api-field-table-mb">
      {rows.map((row, i) => (
        <div className="dp-field-row" key={i}>
          <div className="dp-field-k">{row.k}</div>
          <div className="dp-field-v">{row.v}</div>
        </div>
      ))}
    </div>
  );
}

const code = (text: string) => <span className="api-inline">{text}</span>;

const RATE_LIMIT_BODY = `HTTP/1.1 429 Too Many Requests
Retry-After: 1

{
  "statusCode": 429,
  "message": "Rate limit exceeded (second window)",
  "retryAfterSeconds": 1
}`;

const RATE_LIMIT_GRAPHQL = `HTTP/1.1 429 Too Many Requests
Retry-After: 1

{
  "errors": [
    {
      "message": "Rate limit exceeded (second window)",
      "extensions": { "code": "RATE_LIMITED" }
    }
  ]
}`;

export function ApiKeyCapabilities() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'API key capabilities and limits — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/reference">Reference</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">API key capabilities and limits</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">Reference</span>
        <h1>
          API key <em>capabilities</em> and limits.
        </h1>
        <p className="dp-oneliner">
          What each capability on a key unlocks, which endpoints a key can reach, the default rate
          limits and the 429 response, and how rotation, revocation and tenant binding behave. The
          step-by-step setup is in{' '}
          <Link to="/docs/guides/api-keys">Configure an integration API key</Link>.
        </p>
      </header>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">01</span>
          Capabilities
        </h2>
        <p className="dp-plain">
          A key carries a list of capabilities. Every key holds {code('READ')}. The others are
          opt-in, and none of them stands in for another: a key with {code('WRITE')} cannot mint
          support tokens, and a key with {code('SUPPORT_TOKEN_MINT')} cannot create clients. Asking
          for a capability that does not exist is rejected when the key is issued.
        </p>
        <Table
          rows={[
            {
              k: code('READ'),
              v: (
                <>
                  Always present. Lets the key call the public GraphQL API (queries) and connect to
                  the <Link to="/docs/api/mcp">MCP server</Link> with read tools.
                </>
              ),
            },
            {
              k: code('WRITE'),
              v: (
                <>
                  Public GraphQL mutations that create data: {code('createClient')},{' '}
                  {code('createProspect')} and {code('createSubscription')}. Every mutation that is
                  not named under another capability below requires {code('WRITE')}, so a new
                  mutation is write-gated by default. Over MCP, {code('WRITE')} lets the key
                  propose changes, which a person still approves in Revvly.
                </>
              ),
            },
            {
              k: code('SUPPORT_TOKEN_MINT'),
              v: (
                <>
                  Revvly Support sign-in. Gates {code('createEndUserToken')} and{' '}
                  {code('upsertSupportUsers')} only. It is a server-side secret: the browser only
                  ever holds the short-lived token it mints. See{' '}
                  <Link to="/docs/guides/support-tokens">Sign users in to the support chat</Link>.
                </>
              ),
            },
            {
              k: code('FORM_SUBMIT'),
              v: (
                <>
                  Gates {code('submitForm')}, which sends answers to a published Revvly Form from
                  your backend. It grants no other mutation. See the{' '}
                  <Link to="/docs/api/forms">Forms API</Link>.
                </>
              ),
            },
            {
              k: code('KNOWLEDGE_WRITE'),
              v: (
                <>
                  Gates {code('upsertKnowledgeArticles')}, {code('deleteKnowledgeArticles')} and{' '}
                  {code('finishKnowledgeSync')}, which push help articles into the Support
                  knowledge base. It grants no other mutation. See{' '}
                  <Link to="/docs/guides/support-knowledge-api">the knowledge API guide</Link>.
                </>
              ),
            },
            {
              k: code('PRODUCT_EVENT_WRITE'),
              v: (
                <>
                  Send product usage events ({code('POST /api/v1/product-events')}) and erase one
                  user&rsquo;s events ({code('DELETE /api/v1/product-events/users/:userId')}). No
                  existing key has it. See{' '}
                  <Link to="/docs/api/product-events">the Product Events API</Link>.
                </>
              ),
            },
            {
              k: code('USAGE_WRITE'),
              v: (
                <>
                  Report, set, commission and correct metered usage over REST ({code('POST')}{' '}
                  {code('/api/v1/usage')}, {code('/usage/set')}, {code('/usage/commission')},{' '}
                  {code('/usage/batch')}) and send events ({code('POST /api/v1/events')}). Reading
                  usage only needs {code('READ')}. See{' '}
                  <Link to="/docs/guides/usage-reporting">Usage reporting</Link>.
                </>
              ),
            },
            {
              k: code('PAYMENT_WRITE'),
              v: (
                <>
                  Record payments against invoices through the{' '}
                  <Link to="/docs/api/invoice-payments">invoice payment webhook</Link>. Give it only
                  to the key your payment processor or bank feed holds. Keys that were live on 15
                  October 2026 were given it so existing payment integrations keep working.
                </>
              ),
            },
          ]}
        />
        <p className="dp-plain">
          A mutation request is checked before anything runs. If it names several mutations, the key
          needs every capability they require. A key that lacks one is refused with{' '}
          {code('FORBIDDEN')} (HTTP 403) and the message{' '}
          {code('API key lacks required capability: <names>')}.
        </p>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">02</span>
          What each surface checks
        </h2>
        <p className="dp-plain">
          Capabilities apply to GraphQL and MCP. Over REST, writes to usage and events need{' '}
          {code('USAGE_WRITE')} and the payment webhook needs {code('PAYMENT_WRITE')}; every REST
          endpoint also checks the key itself, its endpoint scope and its subscription and meter
          scope (section 03). {code('WRITE')} grants nothing over REST.
        </p>
        <Table
          rows={[
            {
              k: (
                <>
                  Public GraphQL
                  <br />
                  {code('POST /api/v1/graphql')}
                </>
              ),
              v: 'Queries need READ. Mutations need the capability in section 01. Per-key rate limit applies.',
            },
            {
              k: (
                <>
                  Usage REST
                  <br />
                  {code('/api/v1/usage*')}
                </>
              ),
              v: (
                <>
                  Report and read metered usage. Authenticated by the key; endpoint scope and
                  subscription and meter scope apply. See{' '}
                  <Link to="/docs/guides/usage-reporting">Usage reporting</Link>.
                </>
              ),
            },
            {
              k: (
                <>
                  Event ingestion
                  <br />
                  {code('POST /api/v1/events')}
                </>
              ),
              v: (
                <>
                  Key plus an HMAC signature made with the key&rsquo;s signing secret. See{' '}
                  <Link to="/docs/api/events">Event ingestion</Link>.
                </>
              ),
            },
            {
              k: (
                <>
                  Catalog read
                  <br />
                  {code('GET /api/v1/subscriptions')}, {code('/variables')}, {code('/regions')}
                </>
              ),
              v: (
                <>
                  Discover subscriptions, meters and regions. See{' '}
                  <Link to="/docs/api/external-references">External references</Link>.
                </>
              ),
            },
            {
              k: (
                <>
                  Invoice payment webhook
                  <br />
                  {code('POST /api/v1/webhooks/invoice-payment')}
                </>
              ),
              v: (
                <>
                  Push payment status into Revvly. Needs {code('PAYMENT_WRITE')} and an HMAC
                  signature. See{' '}
                  <Link to="/docs/api/invoice-payments">Invoice payment webhook</Link>.
                </>
              ),
            },
            {
              k: (
                <>
                  MCP
                  <br />
                  {code('POST /mcp')}
                </>
              ),
              v: (
                <>
                  {code('READ')} gives the read tools, {code('WRITE')} adds the proposal tools. The
                  key&rsquo;s endpoint scope must be empty, or include {code('/mcp')} or{' '}
                  {code('*')}. See <Link to="/docs/reference/mcp-tools">the tool catalogue</Link>.
                </>
              ),
            },
          ]}
        />
        <p className="dp-plain">
          The support chat endpoint ({code('/api/v1/support/graphql')}) does not take API keys at
          all. It takes the end-user token that a {code('SUPPORT_TOKEN_MINT')} key mints.
        </p>
        <Figure
          title="What happens to an API call"
          caption="The key decides the tenant, the checks depend on the surface, and a call over its limit is refused with 429."
        >
          <Flow
            steps={[
              { title: 'Key sent', text: 'Bearer key in the Authorization header.' },
              { title: 'Key checked', text: 'Unknown, revoked or expired: 401.', tone: 'danger' },
              {
                fork: [
                  {
                    title: 'GraphQL: capability',
                    text: 'The key must hold what the operation needs, else 403.',
                    tone: 'warn',
                  },
                  {
                    title: 'REST: scope',
                    text: 'Endpoint, subscription and meter scope must allow it, else 403.',
                    tone: 'warn',
                  },
                ],
              },
              { title: 'Rate limit', text: 'Over the per-second or per-minute limit: 429.', tone: 'danger' },
              {
                title: 'Tenant from the key',
                text: 'A conflicting x-tenant-id is refused with 403.',
                tone: 'accent',
              },
            ]}
          />
        </Figure>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">03</span>
          Scope
        </h2>
        <p className="dp-plain">
          Scope narrows a key below the whole workspace. An empty list means all. The issue form in
          the dashboard sets the first two; endpoint scope is set through the {code('issueApiKey')}{' '}
          mutation.
        </p>
        <Table
          rows={[
            {
              k: 'Subscriptions',
              v: 'The subscription ids the key may act on. A request for any other subscription is refused with 403.',
            },
            {
              k: 'Meters',
              v: 'The meter (variable) ids the key may report usage for. The issue form calls this picker Variables.',
            },
            {
              k: 'Endpoints',
              v: (
                <>
                  Path rules such as {code('/usage')} or {code('/usage/set')}, compared without the{' '}
                  {code('/api/v1')} prefix. {code('*')} allows everything, and a trailing wildcard
                  such as {code('/usage/*')} allows everything under that path.
                </>
              ),
            },
          ]}
        />
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            With no scope selected, the dashboard warns that the key has tenant-wide access to all
            subscriptions and meters. Give each integration the narrowest scope it needs.
          </span>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">04</span>
          Rate limits
        </h2>
        <p className="dp-plain">
          Every key has two fixed windows, counted per key: requests per second and requests per
          minute. The check runs after authentication, so unauthenticated traffic never uses a
          key&rsquo;s budget.
        </p>
        <Table
          rows={[
            {
              k: 'Ordinary key',
              v: '10 per second and 100 per minute, unless the issuer sets other values.',
            },
            {
              k: 'Key with SUPPORT_TOKEN_MINT',
              v: '50 per second and 3,000 per minute. One such key signs in every user of your product, so a login rush has more room.',
            },
            {
              k: 'Ceiling on any key',
              v: '500 per second and 30,000 per minute. Both values must be whole numbers of at least 1, and the per-minute value cannot be lower than the per-second value.',
            },
            {
              k: 'Changing a limit',
              v: (
                <>
                  A tenant admin can change a live key&rsquo;s limits with the{' '}
                  {code('updateApiKeyRateLimits')} mutation. The new limits apply from the
                  key&rsquo;s next request, with no rotation. Support secret keys have a limits
                  editor in Support settings. The {code('apiKeyUsage')} query reports a
                  key&rsquo;s requests and refusals over the last 24 hours.
                </>
              ),
            },
          ]}
        />
        <h3 className="api-sub-heading">The 429 response</h3>
        <p className="dp-section-sub">
          Over a limit, the request is refused before it runs. The {code('Retry-After')} header
          carries the seconds to wait. {code('retryAfterSeconds')} says the same in the body. The
          message names the window that was exceeded, {code('second')} or {code('minute')}.
        </p>
        <span className="api-code-label">REST endpoints</span>
        <pre className="api-code">{RATE_LIMIT_BODY}</pre>
        <span className="api-code-label">Public GraphQL</span>
        <pre className="api-code">{RATE_LIMIT_GRAPHQL}</pre>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">05</span>
          Tenant binding
        </h2>
        <p className="dp-plain">
          A key names its workspace. The tenant of a request is the key&rsquo;s tenant, never a
          value you send. Do not add an {code('x-tenant-id')} header to API key calls. If you do and
          it names a different workspace, the request is refused with 403 and the message{' '}
          {code('x-tenant-id does not match the authenticated API key')}. A matching header is
          ignored. This is why a key issued for one workspace can never be aimed at another.
        </p>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">06</span>
          Issue, rotate and revoke
        </h2>
        <p className="dp-plain">
          Only tenant admins and owners can issue, rotate or revoke keys, or change rate limits. Go
          to <strong>Settings &rarr; API Keys</strong>.
        </p>
        <Table
          rows={[
            {
              k: 'Issue',
              v: (
                <>
                  Pick a name and an access level: <strong>Read</strong> (the {code('READ')}{' '}
                  capability only) or <strong>Read and write</strong> ({code('READ')} and{' '}
                  {code('WRITE')}). A <strong>Can submit forms</strong> checkbox adds{' '}
                  {code('FORM_SUBMIT')} and appears when Forms is on for the workspace. A{' '}
                  <strong>Can add knowledge articles</strong> checkbox adds{' '}
                  {code('KNOWLEDGE_WRITE')} and appears when Support is on. Neither needs write
                  access. Support secret keys with {code('SUPPORT_TOKEN_MINT')} are issued from
                  Support settings.
                </>
              ),
            },
            {
              k: 'One-time reveal',
              v: (
                <>
                  The key (it starts {code('rvly_sk_live_')}) and its signing secret are shown once.
                  Only a hash of the key is stored, so neither can be shown again. Lost values mean
                  rotating the key.
                </>
              ),
            },
            {
              k: 'Rotate',
              v: 'Issues a successor with a new key and a new signing secret. The successor keeps the name (with "(rotated)" added), scope, capabilities and rate limits. The old key moves to Rotating and keeps working for a 30-day grace window, then stops with 401 and the message "API key rotation grace period has expired". A revoked key cannot be rotated.',
            },
            {
              k: 'Revoke',
              v: 'Takes effect immediately. The key is refused with 401 and the message "API key has been revoked". A revoked key cannot be restored; issue a new one.',
            },
            {
              k: 'Status',
              v: 'Active, Rotating or Revoked. The key list also shows access, scope and when the key was last used.',
            },
          ]}
        />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">07</span>
          Errors
        </h2>
        <Table
          rows={[
            {
              k: code('401'),
              v: 'No Authorization header, not the Bearer scheme, an unknown key, a revoked key, or a rotating key past its grace window. Unknown and wrong keys get the same generic message on purpose.',
            },
            {
              k: code('403'),
              v: 'The key is outside its endpoint, subscription or meter scope, lacks a capability the operation needs, or sent a conflicting x-tenant-id.',
            },
            {
              k: code('429'),
              v: 'Over the per-second or per-minute limit. Wait the Retry-After seconds.',
            },
          ]}
        />
      </section>

      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/guides/api-keys" style={{ color: 'inherit', textDecoration: 'none' }}>
              Configure an integration API key ›
            </Link>
          </li>
          <li>
            <Link to="/docs/concepts/apiKey" style={{ color: 'inherit', textDecoration: 'none' }}>
              API Key ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/api/graphql-public"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Public GraphQL API ›
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
